import {
  NON_PROD_ENVIRONMENTS,
  configuration,
  environmentName,
} from '../configuration';

/**
 * postMessage contract for handing MSR3's Cognito session to an embedded MSR2
 * frame. The MSR2 half lives in
 * `msr2/packages/ui/src/app/core/services/parent-auth.service.ts` -- keep the
 * two in step.
 *
 * Why this exists: MSR2 is on `screenrights.org` and MSR3 on
 * `screenrights.app`, so the MSR2 iframe is a cross-site frame. Browsers null
 * the site-for-cookies over the whole ancestor chain, so MSR2's
 * `signinSilent()` (a nested frame to the Cognito hosted domain) never sees the
 * SSO cookie and Cognito answers `login_required` -- the user gets a "Login
 * Required" modal inside an app they are already signed in to. MSR3 therefore
 * hands the token over directly. See APP-2221.
 *
 * The `sr-auth-token` message also carries `user_id` and `is_external`
 * (MSR3's member identity, from `hooks/useAnalyticsIdentity.ts`) alongside the
 * Cognito tokens. Both are optional and may be absent from the first send --
 * MSR3 never delays or blocks the token handshake waiting for identity to
 * load. MSR2's GTM reads them off the message and sets them as its own
 * user-scoped properties, since its cookie-based identity is third-party
 * inside the embed and Safari blocks it. See APP-2232.
 *
 * The message may also carry an optional `identity_cleared: true`. This
 * means the member has signed out (or otherwise lost their identity) and
 * MSR2 should clear whatever `user_id` / `is_external` it is holding, rather
 * than "identity has not resolved yet" -- the same thing an absence of both
 * fields, with no flag, means everywhere else. MSR3 only ever sets the flag
 * after it has previously sent real identity fields to that frame; before
 * that, an absence is silence, not a signal.
 *
 * A third state (`null`) was considered and rejected: MSR2's guard for
 * "is this a real user_id" is `typeof data.user_id !== 'string'`, which
 * swallows `null` exactly as it swallows an absent field, so a bare
 * `user_id: null` could not be told apart from "not resolved yet" without
 * MSR2 also tracking its own prior-send state. A dedicated boolean makes the
 * transition explicit on the wire instead, and is additive: an MSR2 that
 * predates this flag ignores the unknown key and simply never clears (its
 * existing behaviour), and a new MSR2 that never receives the flag never
 * takes the new branch either.
 *
 * Delivering the flag on sign-out needs its own route: by the time
 * Amplify's `signOut()` has run (or any later scheduled resend fires),
 * `getCognitoTokens()` returns `null`, and Redux's `userMe` never resets on
 * sign-out either, so nothing about the normal token-send path or the
 * identity-resend effect ever fires for it. `onEmbedSignOut` /
 * `notifyEmbedSignOut` below are the registry `managedLoginSignOut` uses to
 * ask a live handshake to send the clear directly, before the session goes
 * away. See `useEmbedAuthHandshake.ts` and `CognitoManagedLoginProvider.tsx`.
 */
export const EMBED_AUTH_REQUEST_TYPE = 'sr-auth-request';
export const EMBED_AUTH_TOKEN_TYPE = 'sr-auth-token';

/**
 * Put on every embed URL so MSR2 knows a parent is listening and will answer.
 * Without it MSR2 would wait on a handshake that never comes when embedded by
 * anything else.
 *
 * Deliberately a constant, and deliberately the ONLY thing we add: MSR2 works
 * out where to address its request from its own per-environment allowlist, so
 * nothing derived from `window.location` flows into the iframe `src`.
 */
export const PARENT_AUTH_PARAM = 'parentAuth';

// Non-prod MSR2 hosts: the `devmy` apex, PR previews and sandboxes; the
// `my.development` MSR2 embed UI and its `pr-<n>` previews; and a locally run
// MSR2. See MSR2_EMBED_UI_ORIGINS for why the embed UI host is trusted.
const NON_PROD_MSR2_ORIGIN_PATTERNS = [
  /^https:\/\/(?:[a-z0-9-]+\.)?devmy\.screenrights\.org$/,
  /^https:\/\/(?:pr-\d+\.)?my\.development\.screenrights\.app$/,
  /^https?:\/\/localhost(?::\d+)?$/,
];

// Why the embed UI host is trusted (APP-2565): after MSR2's APP-2548 redirect
// the iframe still loads `{MYSCREENRIGHTS_BASE}/embed/...`, which looks the
// member up and then 302s the frame to the Amplify-hosted MSR2 UI on its own
// host. The frame's origin is therefore this one, not MYSCREENRIGHTS_BASE's.
// Keyed by BUILD_ENV with no fallback, so an unknown env gets no extra origin.
// Non-prod envs use the pattern above instead.
const MSR2_EMBED_UI_ORIGINS: Partial<Record<typeof environmentName, string>> = {
  production: 'https://my.screenrights.app',
  staging: 'https://my.staging.screenrights.app',
};

// NON_PROD_ENVIRONMENTS is listed by inclusion, not by excluding
// production/staging: an unknown or future BUILD_ENV must fail closed -- accept
// only the exact configured base -- rather than silently inherit the widened
// origin set above.
const isNonProd = NON_PROD_ENVIRONMENTS.some((env) => env === environmentName);

/**
 * Base URL for MSR2 embeds and cross-app links.
 *
 * Build-time only. To point a local MSR3 at an MSR2 PR preview or sandbox, set
 * `MYSCREENRIGHTS_BASE` in `packages/frontend/.env.local` and restart Vite --
 * see the root README. A runtime (URL parameter) override is deliberately not
 * offered: it would put `window.location` into the iframe `src` and the
 * preconnect `href`, which Snyk Code flags as a DOM-XSS flow.
 */
export const getMsr2Base = (): string =>
  configuration.MYSCREENRIGHTS_BASE.replace(/\/$/, '');

/** Origin form of {@link getMsr2Base}. */
export const getMsr2Origin = (): string => new URL(getMsr2Base()).origin;

/**
 * Whether a `message` event came from an MSR2 frame we trust. The configured
 * base and this env's MSR2 embed UI origin always pass; non-prod additionally
 * accepts any `devmy` PR preview / sandbox, the `my.development` embed UI and
 * its PR previews, and a locally run MSR2.
 *
 * The `my.development` pattern is what non-prod embeds use after MSR2's
 * redirect. The `devmy` preview/sandbox and localhost patterns are headroom,
 * not a capability in use today: pointing MSR3 at a preview is a build-time
 * change (`MYSCREENRIGHTS_BASE`) and the iframe `src` is built from that same
 * constant. They cover an embed URL and the configured base diverging -- a
 * hand-built preview link, a future per-workspace host.
 *
 * This is not the security boundary on its own: it does not check that the
 * message came from the frame we rendered. Message handlers should use
 * {@link isTrustedMsr2Message}, which adds that check.
 */
export const isTrustedMsr2Origin = (origin: string): boolean =>
  origin === getMsr2Origin() ||
  origin === MSR2_EMBED_UI_ORIGINS[environmentName] ||
  (isNonProd &&
    NON_PROD_MSR2_ORIGIN_PATTERNS.some((pattern) => pattern.test(origin)));

/**
 * Whether a `message` event came from the MSR2 frame `iframe` renders. Both
 * checks matter: the origin says who sent it, and the source says it came from
 * the frame we rendered. {@link isTrustedMsr2Origin} accepts a family of
 * origins in non-prod (PR previews, sandboxes, localhost), so a trusted origin
 * alone does not imply our frame.
 *
 * Trust only -- callers still check the payload (`event.data`) themselves.
 */
export const isTrustedMsr2Message = (
  event: MessageEvent,
  iframe: HTMLIFrameElement | null | undefined,
): boolean =>
  isTrustedMsr2Origin(event.origin) && event.source === iframe?.contentWindow;

/** Tag an embed URL so MSR2 knows to ask this window for a token. */
export const withEmbedAuthParams = (url: string): string => {
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${PARENT_AUTH_PARAM}=1`;
};

type EmbedSignOutHandler = () => Promise<void> | void;

// Live embed handshakes (`hooks/useEmbedAuthHandshake.ts`) register
// themselves here while mounted. `managedLoginSignOut` is a plain,
// non-React function with no access to a hook's refs or the iframe it talks
// to -- this registry is its only route to asking a live handshake to push
// `identity_cleared` right now, before Amplify's `signOut()` invalidates the
// session those messages ride on (afterwards `getCognitoTokens()` returns
// `null` and there is nothing left to send). A Set, not a single slot:
// EmbedView and EmbeddedWorkflowView can each mount this hook for their own
// iframe at the same time.
const embedSignOutHandlers = new Set<EmbedSignOutHandler>();

/**
 * Registers `handler` to run on {@link notifyEmbedSignOut}. Returns an
 * unregister function -- callers deregister on unmount so a stale handshake
 * is never invoked.
 */
export function onEmbedSignOut(handler: EmbedSignOutHandler): () => void {
  embedSignOutHandlers.add(handler);

  return () => embedSignOutHandlers.delete(handler);
}

/**
 * Tells every live embed handshake to push `identity_cleared` immediately.
 * Call this BEFORE tearing down the session (see `managedLoginSignOut`).
 * Best-effort: a handshake with no validated origin to reply to, or that
 * never sent identity in the first place, is a no-op (see
 * `useEmbedAuthHandshake.ts`).
 */
export async function notifyEmbedSignOut(): Promise<void> {
  await Promise.all([...embedSignOutHandlers].map((handler) => handler()));
}
